import { useState, useRef, useEffect } from "react";
import { streamPost } from "../lib/api";
import { Send, Loader2, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { track } from "../lib/analytics";
import { ImageAttach } from "../components/ImageAttach";

const GREETINGS = [
  "Hey. I'm your prompt-writing coach. Before I ask you anything: what's ONE thing you've been trying to get an AI to help with lately? Answer in your own words — no jargon required.",
  "Hey. What have you been trying to get an AI to do that it keeps botching? Tell me in whatever words come naturally.",
  "Okay — real talk. What's a task you'd love AI help with but haven't figured out how to ask for? Plain language, please.",
  "Welcome. Tell me a moment where you were fighting with an AI and lost. What were you trying to make it do?",
  "Hi. Skip the pleasantries — what's the last thing you asked an AI where the answer was garbage? What were you actually trying to get?",
];

const genId = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()));

export const Coach = () => {
  const [sessionId] = useState(() => {
    const existing = localStorage.getItem("coach_session");
    if (existing) return existing;
    const id = genId();
    localStorage.setItem("coach_session", id);
    return id;
  });
  const [msgs, setMsgs] = useState(() => [
    { role: "coach", text: GREETINGS[Math.floor(Math.random() * GREETINGS.length)] },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [image, setImage] = useState(null);
  const [showAttach, setShowAttach] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    if ((!input.trim() && !image) || busy) return;
    const userText = input.trim() || "(attached image)";
    const turnIndex = msgs.filter((m) => m.role === "user").length + 1;
    const attachedImage = image; // capture before clearing
    setInput("");
    setImage(null);
    setShowAttach(false);
    setMsgs((m) => [...m, { role: "user", text: userText, image: attachedImage?.dataUrl }, { role: "coach", text: "" }]);
    setBusy(true);
    track("coach_message_sent", { turn: turnIndex, message_length: userText.length, has_image: !!attachedImage });
    try {
      const body = { session_id: sessionId, message: userText };
      if (attachedImage) { body.image_base64 = attachedImage.base64; body.image_mime = attachedImage.mime; }
      await streamPost("/coach", body,
        (_c, full) => setMsgs((m) => { const copy = [...m]; copy[copy.length - 1] = { role: "coach", text: full }; return copy; }),
        () => { setBusy(false); track("coach_message_completed", { turn: turnIndex }); }
      );
    } catch (e) {
      toast.error("coach unavailable: " + e.message);
      track("coach_message_failed", { error: e.message });
      setBusy(false);
    }
  };

  const reset = () => {
    track("coach_session_reset");
    localStorage.removeItem("coach_session");
    window.location.reload();
  };

  return (
    <div data-testid="coach-page" className="mx-auto max-w-4xl px-6 py-16">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--accent-matrix)] mb-3">// module.coach</div>
        <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight">learn in your own words</h1>
        <p className="text-neutral-400 mt-4 max-w-2xl">Socratic dialogue. The coach won&apos;t hand you prompts — it teaches you to ask.</p>
      </div>

      <div className="border border-white/10 bg-[#0a0a0a] flex flex-col h-[65vh]">
        <div ref={scrollRef} data-testid="coach-messages" className="flex-1 overflow-y-auto p-6 space-y-5">
          {msgs.map((m, i) => (
            <div key={`msg-${i}-${m.role}`} data-testid={`coach-msg-${m.role}-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] p-4 text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-[color:var(--accent-amber)] text-black"
                  : "bg-black border border-white/10 text-neutral-200"
              }`}>
                <div className="text-[9px] uppercase tracking-[0.3em] mb-1.5 opacity-60">{m.role === "user" ? "you" : "coach"}</div>
                {m.image && (
                  <img src={m.image} alt="attached" className="mb-2 max-w-full max-h-48 border border-black/20" />
                )}
                {m.text}
                {busy && i === msgs.length - 1 && m.role === "coach" && !m.text && <Loader2 className="w-3 h-3 animate-spin" />}
              </div>
            </div>
          ))}
        </div>

        {showAttach && (
          <div className="border-t border-white/10 p-4 bg-black/50" data-testid="coach-attach-panel">
            <ImageAttach image={image} onChange={setImage} label="attach an image" testIdPrefix="coach" />
          </div>
        )}

        <div className="border-t border-white/10 p-4 flex items-center gap-2">
          <button
            data-testid="coach-attach-toggle"
            onClick={() => setShowAttach((s) => !s)}
            className={`p-2.5 border ${image || showAttach ? "border-[color:var(--accent-matrix)] text-[color:var(--accent-matrix)]" : "border-white/10 text-neutral-500"} hover:border-[color:var(--accent-matrix)] hover:text-[color:var(--accent-matrix)] duration-150`}
            style={{ transitionProperty: "border-color, color" }}
            title="attach image"
            aria-label="attach image"
          >
            <Paperclip className="w-4 h-4" />
          </button>
          <input
            data-testid="coach-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
            placeholder="reply..."
            disabled={busy}
            className="flex-1 bg-black border border-white/10 focus:border-[color:var(--accent-amber)] outline-none px-4 py-2.5 text-sm"
            style={{ transitionProperty: "border-color", transitionDuration: "150ms" }}
          />
          <button
            data-testid="coach-send-btn"
            onClick={send}
            disabled={busy || (!input.trim() && !image)}
            className="bg-[color:var(--accent-amber)] text-black px-4 py-2.5 text-xs uppercase tracking-widest font-bold hover:bg-white disabled:opacity-50 duration-150"
            style={{ transitionProperty: "background-color, opacity" }}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <div className="flex justify-end mt-4">
        <button data-testid="coach-reset" onClick={reset} className="text-[10px] uppercase tracking-widest text-neutral-500 hover:text-[color:var(--accent-flare)] duration-150"
          style={{ transitionProperty: "color" }}>reset session</button>
      </div>
    </div>
  );
};
